import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getRequestEmailThreads } from '../../../api'
import {
  REQUEST_ENTITY,
  SEARCH_RESULTS_REQUESTS_ENTITY,
} from '../../../constants/entity'
import { ARCHIVED_FILTER_NAME } from '../../../constants/filter'
import {
  ROUTE_PATH_LIST,
  SEARCH_RESULTS_REQUESTS_QUERY_PARAM_KEY,
  SUBMENU_DEFAULT_VALUE,
  SUBMENU_PARAM_ARCHIVE_VALUE,
} from '../../../constants/route'
import { useEmailAttachedThreadsModalOpener } from '../../Email'
import { FilterBar } from '../../Filter'
import { Page, PageActions, PageLoader } from '../../Page'
import { PageEmptyState } from '../../PageEmptyState'
import { PageHeader } from '../../PageHeader'
import {
  getRequestAttachedFilesProps,
  RequestTableLayout,
  useRequestTablePage,
} from '../../Request'
import { TableExportData } from '../../Table'
import { TablePagination } from '../../TablePagination'
import { useSearchResultsParams } from '../SearchResults.hooks'
import {
  getSearchFilterStateOptions,
  getSearchResultsBreadcrumbs,
} from '../SearchResults.utils'

import { updateSearchResultsRequestsTableConfig } from './SearchResultsRequests.api'
import {
  QA_NAME_SEARCH_RESULTS_REQUESTS_TABLE,
  SEARCH_RESULTS_FILTER_BAR_ITEMS_PROPS,
  SEARCH_RESULTS_REQUESTS_CONFIG_API,
  SEARCH_RESULTS_REQUESTS_COUNT_API,
  SEARCH_RESULTS_REQUESTS_LIST_API,
} from './SearchResultsRequests.constants'

import * as styles from './SearchResultsRequests.module.css'

import ServiceRequestIcon from '../../../icons/service-request.svg'

function SearchResultsRequests() {
  const { t } = useTranslation()

  const { isArchived, searchQuery } = useSearchResultsParams({
    queryParamKey: SEARCH_RESULTS_REQUESTS_QUERY_PARAM_KEY,
  })

  const asyncTableOptions = {
    ...getSearchFilterStateOptions({
      entity: SEARCH_RESULTS_REQUESTS_ENTITY,
      isArchived,
      searchQuery,
      sub: SUBMENU_DEFAULT_VALUE,
    }),
    countApi: SEARCH_RESULTS_REQUESTS_COUNT_API,
    extraFilters: {
      [ARCHIVED_FILTER_NAME]: isArchived,
      [SEARCH_RESULTS_REQUESTS_QUERY_PARAM_KEY]: searchQuery,
    },
    listApi: SEARCH_RESULTS_REQUESTS_LIST_API,
    tableConfigApi: SEARCH_RESULTS_REQUESTS_CONFIG_API,
    withoutSubMenu: true,
  }

  const extraSelectionResetDeps = [isArchived, searchQuery]

  const [modal, setModal] = useState(null)

  function handleCloseModal() {
    setModal(null)
  }

  const { handleClickEmailTag } = useEmailAttachedThreadsModalOpener({
    counterTextKey: 'Modal.AttachedEmailsCountBtnKey',
    getAttachedFilesProps: getRequestAttachedFilesProps,
    headerTextKey: 'Modal.RequestEmailAttachedThreadsTitleKey',
    loadItems: getRequestEmailThreads,
    onCloseModal: handleCloseModal,
    onSetModal: setModal,
  })

  const tablePage = useRequestTablePage({
    actionsSub: isArchived
      ? SUBMENU_PARAM_ARCHIVE_VALUE
      : SUBMENU_DEFAULT_VALUE,
    asyncTableOptions,
    entity: SEARCH_RESULTS_REQUESTS_ENTITY,
    extraSelectionResetDeps,
    handleClickEmailTag,
    pageTitle: t('SearchResults.RequestsBrowserTabTitleKey'),
    setModal,
    sub: SUBMENU_DEFAULT_VALUE,
    onApplyTableConfig: updateSearchResultsRequestsTableConfig,
    onCloseModal: handleCloseModal,
  })

  const {
    availableFilters,
    columns,
    exportOptions,
    filterValues,
    handleChangeShouldReloadFilters,
    handleLoadInit,
    handleOpenExportModal,
    handleReload,
    handleSetFilterValues,
    handleSetPagination,
    isLoading,
    isPerformingMassAction,
    isRefetching,
    items: requests,
    ordering,
    pageContentRef,
    pageScrollWrapperRef,
    pagination,
    permissions,
    requiredColumns,
    serverError,
    shouldReloadFilters,
  } = tablePage

  useEffect(() => {
    handleLoadInit()
  }, [])

  const hasRequests = requests?.items?.length > 0
  const isTableLoading = isLoading || isRefetching

  const breadcrumbs = getSearchResultsBreadcrumbs({
    leadingBreadcrumbs: [
      {
        id: 'requests',
        title: t('RequestTable.PageTitleKey'),
        to: `/${ROUTE_PATH_LIST.serviceRequests}/`,
      },
    ],
    searchQuery,
    t,
  })

  return columns && requiredColumns ? (
    <Page
      contentRef={pageContentRef}
      pagination={
        <TablePagination
          responsive
          isSkeleton={isLoading}
          value={pagination}
          onChange={handleSetPagination}
        />
      }
      qaName={QA_NAME_SEARCH_RESULTS_REQUESTS_TABLE}
      scrollWrapperRef={pageScrollWrapperRef}
      serverError={serverError}
    >
      {isPerformingMassAction ? <PageLoader /> : null}
      <PageHeader
        breadcrumbs={breadcrumbs}
        isLoading={isLoading}
        itemsCount={pagination.count}
      >
        {permissions.can_export || isLoading ? (
          <PageActions className={styles.pageActions}>
            <TableExportData
              actions={exportOptions}
              buttonProps={{
                className: styles.exportButton,
                textClassName: styles.buttonTextResponsive,
              }}
              containerRef={pageContentRef}
              isSkeleton={isLoading}
              title='RequestTable.ExportBtnKey'
              onClickAction={handleOpenExportModal}
            />
          </PageActions>
        ) : null}
      </PageHeader>
      {availableFilters.length > 0 || isLoading ? (
        <FilterBar
          responsive
          availableFilters={availableFilters}
          canViewFilterTemplate={false}
          entity={REQUEST_ENTITY}
          filterBarItemsProps={SEARCH_RESULTS_FILTER_BAR_ITEMS_PROPS}
          filterValues={filterValues}
          isSkeleton={isLoading}
          ordering={ordering}
          pagination={{ per_page: pagination.pageSize }}
          setFilterValues={handleSetFilterValues}
          shareBodyExtras={{
            [ARCHIVED_FILTER_NAME]: isArchived,
            [SEARCH_RESULTS_REQUESTS_QUERY_PARAM_KEY]: searchQuery,
          }}
          shareEntity={SEARCH_RESULTS_REQUESTS_ENTITY}
          shouldReloadValues={shouldReloadFilters}
          sub={SUBMENU_DEFAULT_VALUE}
          onChangeShouldReloadValues={handleChangeShouldReloadFilters}
          onReload={handleReload}
        />
      ) : null}
      <RequestTableLayout
        emptyState={
          <PageEmptyState
            EntityIcon={ServiceRequestIcon}
            text={t('Request.NoDataKey')}
          />
        }
        entity={SEARCH_RESULTS_REQUESTS_ENTITY}
        hasTable={hasRequests || isTableLoading}
        modal={modal}
        tablePage={{ ...tablePage, isLoading: isTableLoading }}
      />
    </Page>
  ) : null
}

export default SearchResultsRequests
